import {
  Avatar,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { useFieldArray, UseFormSetValue } from 'react-hook-form'
import {
  DateTimePickerField,
  Icons,
  TextInputField,
  TextInputFieldProps,
  ToggleInputField,
} from 'utoniq-ui'

import {
  ArtistBannerDetailFragment,
  ArtistUpdateArtistBannerInput,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { FileUploadInfo } from '@/components/pages/ConsoleCollectionCreate/template/CollectionCreateForm'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import {
  validateBannerDateOrder,
  validatePublicBannerEndDate,
  validatePublicBannerOverlap,
} from '@/core/validators'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

import { ReservationSubmitValues } from './ConsoleCampaignReservationBannerSettingForm'

export type ConsoleCampaignBannerSettingFormProps = {
  loading: boolean
  bannerSettings: Maybe<ArtistBannerDetailFragment[]>
  onSubmit: (
    inputs: SubmitValues | ReservationSubmitValues,
    isReservationBanners?: boolean
  ) => Promise<void>
  fileUploadInfo: FileUploadInfo
  isReservationBanners?: boolean
  reservationBannerStartedAt?: string
  reservationBannerEndedAt?: string
}

export type SubmitValues = {
  banners: ArtistUpdateArtistBannerInput[]
  bannersStartedAt: string
  bannersEndedAt: string
  bannersStatus: boolean
  reservationBannerStartedAt?: string
  reservationBannerEndedAt?: string
}

const schema: UseFormProps['schema'] = o => ({
  banners: o.array().items(
    o.object({
      tmpPath: o.string().allow('').optional(),
      link: o.object().when('tmpPath', {
        is: '',
        then: o
          .object({
            externalUrl: o.string().optional().allow(''),
            innerPath: o.string().optional().allow(''),
          })
          .optional(),
        otherwise: o.object({
          externalUrl: o
            .string()
            .label(tLabel('consoleCampaignBannerSetting.link.externalUrl')),
          innerPath: o.string().optional().allow(''),
        }),
      }),
    })
  ),
  bannersStartedAt: o
    .date()
    .allow(null, '')
    .optional()
    .custom((value: Date, helpers) => {
      if (!value) {
        return value
      }
      const [data] = helpers?.state?.ancestors || []

      // 開始日・終了日の順序チェック
      const dateOrderResult = validateBannerDateOrder(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'bannersEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.invalidDateOrder'
      )
      if (dateOrderResult !== value) {
        return dateOrderResult
      }

      // 予約バナーとの重複チェック
      const overlapResult = validatePublicBannerOverlap(
        value,
        helpers,
        data,
        'reservationBannerStartedAt',
        'reservationBannerEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.overlapReservation'
      )
      if (overlapResult !== value) {
        return overlapResult
      }

      // 予約バナーとの前後関係チェック
      const orderResult = validatePublicBannerEndDate(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'reservationBannerStartedAt',
        'reservationBannerEndedAt',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.overlapReservation',
        'consoleCampaignBannerSetting.bannersStartedAt.custom.publicBeforeReservation'
      )
      if (orderResult !== value) {
        return orderResult
      }

      return value
    })
    .label(tLabel('consoleCampaignBannerSetting.startedAt')),
  bannersEndedAt: o
    .date()
    .allow(null, '')
    .optional()
    .custom((value: Date, helpers) => {
      if (!value) {
        return value
      }
      const [data] = helpers?.state?.ancestors || []

      // 開始日・終了日の順序チェック
      const dateOrderResult = validateBannerDateOrder(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'bannersEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.invalidDateOrder'
      )
      if (dateOrderResult !== value) {
        return dateOrderResult
      }

      // 予約バナーとの重複チェック
      const overlapResult = validatePublicBannerOverlap(
        value,
        helpers,
        data,
        'reservationBannerStartedAt',
        'reservationBannerEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.overlapReservation'
      )
      if (overlapResult !== value) {
        return overlapResult
      }

      // 予約バナーとの前後関係チェック
      const endDateResult = validatePublicBannerEndDate(
        value,
        helpers,
        data,
        'bannersStartedAt',
        'reservationBannerStartedAt',
        'reservationBannerEndedAt',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.overlapReservation',
        'consoleCampaignBannerSetting.bannersEndedAt.custom.publicBeforeReservation'
      )
      if (endDateResult !== value) {
        return endDateResult
      }

      return value
    })
    .label(tLabel('consoleCampaignBannerSetting.endedAt')),
  bannersStatus: o.boolean().allow(false).optional(),
  reservationBannerStartedAt: o.date().allow(null, '').optional(),
  reservationBannerEndedAt: o.date().allow(null, '').optional(),
})

export const ConsoleCampaignBannerSettingForm: Organism<ConsoleCampaignBannerSettingFormProps> =
  ({
    loading,
    bannerSettings,
    onSubmit,
    fileUploadInfo,
    isReservationBanners,
    reservationBannerStartedAt,
    reservationBannerEndedAt,
  }) => {
    const { t } = useTranslation('consoleCampaignBannerSetting')
    const { showMessage, showError } = useMessage()
    const {
      handleSubmit,
      register,
      setValue,
      reset,
      setError,
      control,
      watch,
      formState: { errors, isSubmitting },
    } = useForm<SubmitValues>({
      schema,
      defaultValues: {
        ...defaultValues,
      },
    })

    const { fields } = useFieldArray({ control, name: 'banners' })

    useEffect(() => {
      // isReservationBannersが変更された時にフォームをリセット
      reset(defaultValues)
    }, [isReservationBanners, reset])

    useEffect(() => {
      // 予約バナーの値を設定
      if (reservationBannerStartedAt || reservationBannerEndedAt) {
        setValue('reservationBannerStartedAt', reservationBannerStartedAt || '')
        setValue('reservationBannerEndedAt', reservationBannerEndedAt || '')
      }
      if (!bannerSettings?.length) {
        reset({
          ...defaultValues,
          reservationBannerStartedAt: reservationBannerStartedAt || '',
          reservationBannerEndedAt: reservationBannerEndedAt || '',
        })
        return
      }
      reset({
        banners: defaultValues.banners?.map((_, index) => ({
          link: {
            externalUrl:
              bannerSettings[index]?.link?.externalUrl ??
              bannerSettings[index]?.link?.innerPath ??
              '',
            innerPath: '',
          },
          tmpPath: bannerSettings[index]?.thumbnail.uri ?? '',
        })),
        bannersStartedAt: bannerSettings[0]?.bannersStartedAt ?? '',
        bannersEndedAt: bannerSettings[0]?.bannersEndedAt ?? '',
        bannersStatus: bannerSettings[0]?.bannerStatus === 'published' || false,
        reservationBannerStartedAt: reservationBannerStartedAt ?? '',
        reservationBannerEndedAt: reservationBannerEndedAt ?? '',
      })
    }, [
      bannerSettings,
      reset,
      reservationBannerStartedAt,
      reservationBannerEndedAt,
      setValue,
    ])

    const handleOnSubmit = handleSubmit((values: SubmitValues) => {
      return onSubmit(values, isReservationBanners)
        .then(() => {
          showMessage(t('registerBannerSuccess'), 'success')
        })
        .catch(e => {
          handleError<SubmitValues>(e, setError, showError)
        })
    })

    return (
      <Form onSubmit={handleOnSubmit}>
        <Stack>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              mb: 2,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {t('addedBannerImage', 'バナー画像を追加')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t(
              'campaignBannerSettingDesc',
              'サービストップ画面に表示するバナーを最大3つまで登録することができます。'
            )}
          </Typography>
        </Stack>
        {loading ? (
          <Stack spacing={5}>
            {defaultValues.banners?.map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                sx={{ borderRadius: 2 }}
                width="100%"
                height={124}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={5}>
            {fields.map((field, index) => (
              <CampaignBannerCell
                key={field.id}
                index={index}
                thumbnailUploadInfo={fileUploadInfo}
                setValue={setValue}
                defaultValue={bannerSettings?.[index]?.thumbnail?.uri ?? ''}
                inputProps={{
                  ...register(`banners.${index}.link.externalUrl`),
                  error: errors.banners?.[index]?.link?.externalUrl,
                  type: 'text',
                }}
              />
            ))}
            <ToggleInputField
              checked={watch('bannersStatus')}
              onChange={() => {
                const currentValue = watch('bannersStatus')
                setValue('bannersStatus', !currentValue, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }}
              content={{
                title: t('bannerStatus'),
                description: (
                  <Typography
                    sx={{
                      width: 335,
                      height: 60,
                      fontSize: 13,
                      color: 'text.secondary',
                    }}
                  >
                    {t('bannerStatusDesc')}
                  </Typography>
                ),
              }}
              sx={{
                px: 0,
                pt: 0,
                '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                  bgcolor: 'primary.main',
                },
              }}
            />
            <Stack spacing={3}>
              <DateTimePickerField
                onChange={date => {
                  setValue(
                    'bannersStartedAt',
                    (convertDatePickerValueToDate(date) || '') as never,
                    { shouldValidate: true, shouldDirty: true }
                  )
                }}
                value={
                  watch('bannersStartedAt')
                    ? dayjs(watch('bannersStartedAt'))
                    : null
                }
                title={t('startedAt')}
                description={t('startedAtDesc')}
                placeholder={t('startedAtPlaceholder')}
                error={errors?.bannersStartedAt}
                titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
              />
              <DateTimePickerField
                onChange={date => {
                  setValue(
                    'bannersEndedAt',
                    (convertDatePickerValueToDate(date) || '') as never,
                    { shouldValidate: true, shouldDirty: true }
                  )
                }}
                value={
                  watch('bannersEndedAt')
                    ? dayjs(watch('bannersEndedAt'))
                    : null
                }
                title={t('endedAt')}
                description={t('endedAtDesc')}
                placeholder={t('endedAtPlaceholder')}
                error={errors?.bannersEndedAt}
                titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
              />
            </Stack>
          </Stack>
        )}
        <CellFrame sx={{ py: 5, textAlign: 'center' }}>
          <Button
            type="submit"
            size="large"
            sx={{ width: 244 }}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('submit', '登録する')}
          </Button>
        </CellFrame>
      </Form>
    )
  }

type CampaignBannerCellProps = {
  index: number
  defaultValue: string
  inputProps: Omit<TextInputFieldProps, 't'>
  thumbnailUploadInfo: FileUploadInfo
  setValue: UseFormSetValue<SubmitValues>
}

const CampaignBannerCell = ({
  index,
  defaultValue,
  inputProps,
  thumbnailUploadInfo,
  setValue,
}: CampaignBannerCellProps) => {
  const { t } = useTranslation('consoleBusinessEdit')
  const [imageUrl, setImageUrl] = useState<string>('')

  useEffect(() => {
    setImageUrl(defaultValue)
  }, [defaultValue])

  const handleUploadFile = useCallback(async () => {
    const result = await thumbnailUploadInfo.onDrop()
    if (!result) return
    setImageUrl(result.imageUrl ?? '')
    setValue(`banners.${index}.tmpPath`, result.imageUploadPath ?? '', {
      shouldValidate: true,
    })
  }, [thumbnailUploadInfo, setValue, index])

  const handleRemoveUploadFile = useCallback(() => {
    setValue(`banners.${index}.tmpPath`, '')
    setValue(`banners.${index}.link.externalUrl`, '')
    setImageUrl('')
  }, [setValue, index])

  return (
    <CellFrame sx={{ display: 'flex', justifyContent: 'center' }}>
      <Card
        elevation={0}
        sx={{
          borderWidth: 1,
          borderColor: 'grey.100',
          borderStyle: 'dashed',
          bgcolor: 'transparent',
          borderRadius: 2,
          width: imageUrl ? '100%' : 'auto',
        }}
      >
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            {!!imageUrl && (
              <Button
                variant="text"
                startIcon={<Icons.Trash color="error" />}
                color="error"
                onClick={handleRemoveUploadFile}
              >
                {t('delete', '削除する')}
              </Button>
            )}
          </Stack>
          <Stack sx={{ pt: 2 }}>
            {imageUrl ? (
              <Avatar
                variant="square"
                src={imageUrl}
                sx={{ height: 124, width: '100%' }}
              />
            ) : (
              <Stack spacing={2} alignItems="center">
                <Icons.Drop
                  sx={{
                    width: 56,
                    height: 46,
                    color: 'grey.400',
                  }}
                />
                <Typography variant="caption" color="text.hint">
                  {t(
                    'addedBannerImageDesc',
                    '幅750px 高さ315px / PNG・JPEG・HEICファイル'
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ width: 'fit-content' }}
                  onClick={handleUploadFile}
                >
                  {t('selectFiles', 'ファイルを選択')}
                </Button>
              </Stack>
            )}
          </Stack>
          {!!imageUrl && (
            <Stack sx={{ pt: 5 }}>
              <TextInputCell {...inputProps} />
            </Stack>
          )}
        </CardContent>
      </Card>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  ({ titleProps, error, ...props }: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleBusinessEdit')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          titleProps={{
            variant: 'h6',
            color: 'text.primary',
            children: t('linkURL', 'リンク先のURL'),
          }}
          textFieldProps={{
            sx: { mb: 0, fieldset: { border: 'none' }, input: { p: 0 } },
            helperText: error?.message,
            placeholder: t(
              'inputLinkURLPlaceholder',
              '入力してください (例 https://xxxxxx)'
            ),
          }}
          error={error}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)
const defaultValues: SubmitValues = {
  banners: [
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
    {
      link: {
        externalUrl: '',
        innerPath: '',
      },
      tmpPath: '',
    },
  ],
  bannersStartedAt: '',
  bannersEndedAt: '',
  bannersStatus: false,
  reservationBannerStartedAt: '',
  reservationBannerEndedAt: '',
}
